// Disputes — public ledger of every structural dispute filed against
// submissions, citations, or sealed block leaves.
//
// v19.30 · Any signed-in observer can file a dispute via the
// DisputeButton component; each filing writes three cryptographic
// traces (disputes ledger row, target's challenges array, Never-Harm
// event). This page renders the disputes ledger as a public, walk-
// verifiable feed.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  AlertOctagon,
  Filter,
  Loader2,
  ShieldQuestion,
  RefreshCw,
  Copy,
  Check,
  Anchor,
} from "lucide-react";
import { toast } from "sonner";
import DisputeBtn from "../components/sentinel/DisputeButton";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso.slice(0, 16);
  }
}

function short(h, n = 10) {
  if (!h) return "—";
  if (h.length <= n * 2 + 1) return h;
  return `${h.slice(0, n)}…${h.slice(-4)}`;
}

function CopyHash({ value, testid }) {
  const [done, setDone] = useState(false);
  if (!value) return <span className="text-zinc-700 font-mono text-[11px]">—</span>;
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1000);
        } catch {
          toast.error("Clipboard unavailable");
        }
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-zinc-300 hover:text-emerald-300 border border-zinc-800 hover:border-emerald-500/60 px-1.5 py-0.5"
    >
      {done ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3 opacity-50" />}
      {short(value)}
    </button>
  );
}

const STANCE_CLS = {
  refute: "border-rose-500/60 text-rose-300 bg-rose-500/[0.05]",
  qualify: "border-amber-500/60 text-amber-300 bg-amber-500/[0.05]",
};

const TARGET_CLS = {
  submission: "border-violet-500/60 text-violet-300",
  citation: "border-cyan-500/60 text-cyan-300",
  block_leaf: "border-emerald-500/60 text-emerald-300",
  dispute: "border-fuchsia-500/60 text-fuchsia-300",
};

export default function Disputes() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStance, setFilterStance] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterType) params.set("target_type", filterType);
      if (filterStance) params.set("stance", filterStance);
      const res = await fetch(`${API}/api/pgrl/disputes?${params.toString()}`);
      const data = await res.json();
      setRows(data.disputes || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filterType, filterStance]);

  return (
    <div
      className="min-h-screen bg-[#09090b] text-zinc-100"
      data-testid="disputes-page"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <Link
          to="/"
          data-testid="disputes-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-3 font-mono">
            <AlertOctagon className="h-3 w-3" /> Disputes ledger · v19.30
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            Every fact stays disputable. Forever.
          </h1>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-3xl">
            Any signed-in observer can file a structural challenge against a
            submission, citation, sealed block leaf, <em className="text-fuchsia-300 not-italic">or another dispute</em>.
            Each filing writes three cryptographic traces: a row in this
            ledger (HMAC-chained), a challenge appended to the target&apos;s own
            audit trail, and a Never-Harm event on the master chain.
            Refutations are not deletions — they are additions.
            <br /><br />
            <span className="text-emerald-300">A truth found later can dispute
            a truth found earlier. A dispute can be countered by another
            dispute.</span> The record grows; it never gets rewritten. As the
            sandbox expands and new truths surface, the primitive holds: no
            claim is ever locked-in beyond challenge.
          </p>
        </div>

        {/* Filters + stats */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="disputes-toolbar">
          <div className="inline-flex items-center gap-1.5 border border-zinc-800 px-3 py-1.5">
            <Filter className="h-3 w-3 text-zinc-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
              type:
            </span>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-transparent text-[11px] font-mono text-zinc-200 outline-none"
              data-testid="disputes-filter-type"
            >
              <option value="">all</option>
              <option value="submission">submission</option>
              <option value="citation">citation</option>
              <option value="block_leaf">block_leaf</option>
              <option value="dispute">dispute (counter)</option>
            </select>
          </div>
          <div className="inline-flex items-center gap-1.5 border border-zinc-800 px-3 py-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
              stance:
            </span>
            <select
              value={filterStance}
              onChange={(e) => setFilterStance(e.target.value)}
              className="bg-transparent text-[11px] font-mono text-zinc-200 outline-none"
              data-testid="disputes-filter-stance"
            >
              <option value="">all</option>
              <option value="refute">refute</option>
              <option value="qualify">qualify</option>
            </select>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-emerald-500/60 hover:text-emerald-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-400"
            data-testid="disputes-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            refresh
          </button>
          <div className="ml-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-mono">
            <ShieldQuestion className="h-3 w-3 text-amber-400" />
            {total} disputes filed
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="border border-zinc-800 bg-[#0b0b0d] px-6 py-12 flex items-center justify-center gap-3 text-zinc-500 font-mono text-[11px] uppercase tracking-[0.3em]">
            <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
            loading ledger…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="border border-zinc-800 bg-[#0b0b0d] px-6 py-16 text-center"
            data-testid="disputes-empty"
          >
            <ShieldQuestion className="h-8 w-8 text-zinc-700 mx-auto mb-3" />
            <div className="text-zinc-500 font-mono text-[11px] uppercase tracking-[0.3em]">
              no disputes match this filter yet
            </div>
          </div>
        ) : (
          <div className="border border-zinc-800 bg-[#0b0b0d] divide-y divide-zinc-900">
            {rows.map((d, i) => (
              <div
                key={d.dispute_id}
                className="p-5"
                data-testid={`dispute-row-${i}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span
                    className={`px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.28em] ${
                      STANCE_CLS[d.stance] || "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    {d.stance}
                  </span>
                  <span
                    className={`px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.28em] ${
                      TARGET_CLS[d.target_type] || "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    {d.target_type === "dispute" ? "↻ counter" : d.target_type}
                  </span>
                  <span className="text-[10px] text-zinc-500 font-mono">
                    target: <span className="text-zinc-300">{short(d.target_id, 8)}</span>
                  </span>
                  {d.references_dispute_id ? (
                    <span className="text-[10px] text-fuchsia-400 font-mono">
                      ↻ counters {short(d.references_dispute_id, 8)}
                    </span>
                  ) : null}
                  <span className="ml-auto text-[10px] text-zinc-500 font-mono">
                    {formatDate(d.filed_at)}
                  </span>
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed mb-3">
                  {d.reason}
                </p>
                {d.evidence_url ? (
                  <a
                    href={d.evidence_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-[11px] font-mono text-emerald-300 hover:text-emerald-100 underline decoration-emerald-500/40 mb-3 break-all"
                    data-testid={`dispute-evidence-${i}`}
                  >
                    {d.evidence_url}
                  </a>
                ) : null}
                <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zinc-900 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Anchor className="h-3 w-3 text-emerald-400" />
                    id:
                  </span>
                  <CopyHash value={d.dispute_id} testid={`dispute-id-${i}`} />
                  <span className="inline-flex items-center gap-1.5">
                    hash:
                  </span>
                  <CopyHash value={d.hash} testid={`dispute-hash-${i}`} />
                  <span className="inline-flex items-center gap-1.5">
                    prev:
                  </span>
                  <CopyHash value={d.prev_hash} testid={`dispute-prev-${i}`} />
                  <div className="ml-auto flex items-center gap-2">
                    <span className="normal-case tracking-normal">
                      by {d.filed_by_email ? short(d.filed_by_email, 12) : short(d.filed_by_user_id, 10)}
                    </span>
                    <DisputeBtn
                      targetType="dispute"
                      targetId={d.dispute_id}
                      targetLabel={`Counter this dispute: "${d.reason.slice(0, 100)}${d.reason.length > 100 ? "…" : ""}"`}
                      onFiled={load}
                      compact
                      testidPrefix={`counter-dispute-${i}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 text-[10px] text-zinc-600 font-mono uppercase tracking-[0.22em]">
          Every row is HMAC-signed against the prior row&apos;s hash. Verify one
          via <span className="text-emerald-300">GET /api/pgrl/disputes/&lt;dispute_id&gt;</span>.
        </div>
      </div>
    </div>
  );
}
